import React, { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { BeatLoader } from "react-spinners";
import ModalDialog from "../common/ModalDialog";
import tailwindClassnames from "../common/TailwindClassnames";
import ProjectTaxonPicker from "./ProjectTaxonPicker";
import { addSpeciesListItem } from "../../api/projectsApi";
import { TaxonomyResponseDto } from "../common/ObjectTypes";

type AddSpeciesModalProps = {
  open: boolean;
  projectId: number;
  /** Pre-fills the taxon picker, e.g. when the per-row Add species action is
   *  opened from a mapping-only row. */
  initialTaxonId?: number | null;
  initialTaxonLabel?: string | null;
  /** Project-wide status per taxon_id, for the picker's annotated results. */
  taxonStatus: Map<number, "in" | "excluded">;
  /** Taxa the current model already has a raw-label mapping for — drives the
   *  helper note explaining whether the new species will show as a normal
   *  (label-backed) row or a manual-only one. */
  labelTaxonIds: Set<number>;
  onClose: () => void;
  /** Called after a successful add so the parent refetches. */
  onSaved: () => void;
};

/**
 * "Add species" dialog: adds a taxon (with an optional label override) to the
 * project's explicit species list, for taxa the model can't predict or to
 * make a mapping-only taxon's membership explicit.
 */
const AddSpeciesModal: React.FC<AddSpeciesModalProps> = ({
  open,
  projectId,
  initialTaxonId = null,
  initialTaxonLabel = null,
  taxonStatus,
  labelTaxonIds,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const titleId = useId();

  const [taxonId, setTaxonId] = useState<number | null>(null);
  const [taxonDetail, setTaxonDetail] = useState<TaxonomyResponseDto | null>(
    null,
  );
  const [label, setLabel] = useState("");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTaxonId(initialTaxonId ?? null);
    setTaxonDetail(null);
    setLabel("");
    setFormError("");
  }, [open, initialTaxonId]);

  if (!open) return null;

  const handleSave = async () => {
    if (saving) return;
    if (taxonId == null) {
      setFormError(t("Select a taxon"));
      return;
    }
    setSaving(true);
    try {
      await addSpeciesListItem(projectId, {
        taxon_id: taxonId,
        label: label.trim() === "" ? null : label.trim(),
      });
      toast.success(t("Species added"));
      onSaved();
      onClose();
    } catch (error: any) {
      console.error("Error adding species:", error);
      if (error?.status) {
        toast.error(error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const alreadyHasLabel = taxonId != null && labelTaxonIds.has(taxonId);
  const speciesName =
    taxonDetail?.common_name_english ?? initialTaxonLabel ?? "";

  const addNote =
    taxonId == null
      ? t(
          "Manual species count toward your species list but not the model's label total.",
        )
      : alreadyHasLabel
        ? t(
            "{{name}} already has a model label, so it will appear as a normal mapping rather than a manual-only entry.",
            { name: speciesName },
          )
        : t(
            "This model can't predict {{name}}, so it goes in as manual-only — reviewers can still tag it. It counts toward your species list but not the model's label total.",
            { name: speciesName },
          );

  return (
    <ModalDialog
      onClose={onClose}
      busy={saving}
      labelledBy={titleId}
      overlayClassName="z-50"
      panelClassName="max-w-3xl"
    >
      <>
        <div className={tailwindClassnames.modalHeaderRow}>
          <h2 id={titleId} className={tailwindClassnames.modalTitle}>
            {t("Add species")}
          </h2>
          <button
            type="button"
            className={tailwindClassnames.modalCloseButton}
            onClick={onClose}
            aria-label={t("Close")}
          >
            &times;
          </button>
        </div>
        <div className={`${tailwindClassnames.modalBody} space-y-4`}>
          <p className={`${tailwindClassnames.helperText} !mt-0`}>
            {t(
              "For taxa the model can't predict. These appear in the table sorted by species, with no raw label.",
            )}
          </p>

          <ProjectTaxonPicker
            name={t("Taxon")}
            value={taxonId}
            initialLabel={initialTaxonLabel}
            onChange={(id) => {
              setTaxonId(id);
              if (id != null) setFormError("");
            }}
            taxonStatus={taxonStatus}
            cardLabel={t("Species to add")}
            searchLabel={t("Search the taxonomy")}
            showExcludedWarning={false}
            onSelectedDetailChange={setTaxonDetail}
          />
          {formError && (
            <p className="bg-primary-orange-light rounded text-primary-orange-dark text-sm px-3 py-2">
              {formError}
            </p>
          )}

          <p className="text-xs text-off-black-light dark:text-off-white-dark bg-off-white-dark dark:bg-storm-blue-light rounded px-3 py-2">
            {addNote}
          </p>

          <div className="flex flex-col gap-1">
            <label
              htmlFor="species-label"
              className={tailwindClassnames.textInputLabel}
            >
              {t("Name reviewers see")}{" "}
              <span className="text-greyed-text font-normal">
                — {t("optional")}
              </span>
            </label>
            <input
              id="species-label"
              type="text"
              className={tailwindClassnames.detailsPageTextInput}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
        </div>

        <div className={tailwindClassnames.modalFooter}>
          <button
            type="button"
            className={tailwindClassnames.btnSecondary}
            onClick={onClose}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className={`relative ${tailwindClassnames.btnPrimary}`}
            onClick={handleSave}
            disabled={saving}
            data-testid="save-species-button"
          >
            {/* Label stays in layout while saving so the button keeps its
                width; currentColor spinner reads on the primary fill in both
                themes. */}
            <span className={saving ? "invisible" : ""}>
              {t("Add species")}
            </span>
            {saving && (
              <span className="absolute inset-0 flex items-center justify-center">
                <BeatLoader
                  color="currentColor"
                  loading={true}
                  size={10}
                  margin={5}
                />
              </span>
            )}
          </button>
        </div>
      </>
    </ModalDialog>
  );
};

export default AddSpeciesModal;
